'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { ShoppingBag, Menu, X, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCartStore } from '@/store';
import Image from 'next/image';
import CurrencySwitcher from '@/components/shop/CurrencySwitcher';
import styles from './Header.module.css';

export default function Header() {
    const router = useRouter();
    const pathname = usePathname();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const tapCountRef = useRef(0);
    const tapTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [isMounted, setIsMounted] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        setIsMounted(true);
        const onScroll = () => setScrolled(window.scrollY > 72);
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const { getTotalItems, toggleCart } = useCartStore();
    const totalItems = isMounted ? getTotalItems() : 0;

    // Hidden admin access: 3 taps on logo
    const handleLogoClick = useCallback(() => {
        tapCountRef.current += 1;

        if (tapTimeoutRef.current) {
            clearTimeout(tapTimeoutRef.current);
        }

        if (tapCountRef.current >= 3) {
            tapCountRef.current = 0;
            router.push('/admin/login');
            return;
        }

        // Navigate home immediately, 3 fast taps will still trigger admin
        if (tapCountRef.current === 1) {
            router.push('/');
        }

        tapTimeoutRef.current = setTimeout(() => {
            tapCountRef.current = 0;
        }, 500);
    }, [router]);

    const navLinks = [
        { href: '/', label: 'Home' },
        { href: '/products', label: 'Products' },
        { href: '/about', label: 'About' },
        { href: '/contact', label: 'Contact' },
    ];

    return (
        <header className={`${styles.header} ${scrolled ? styles.scrolled : styles.transparent}`}>
            <div className={styles.container}>
                {/* Logo with hidden admin access */}
                <div
                    className={styles.logo}
                    onClick={handleLogoClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleLogoClick()}
                >
                    <Image
                        src="/logo-with-text.png"
                        alt="Sirius Coffee Logo"
                        width={140}
                        height={50}
                        sizes="140px"
                        className={styles.logoImage}
                        style={{ objectFit: 'contain' }}
                        priority
                        loading="eager"
                    />
                </div>

                <nav className={styles.desktopNav}>
                    {navLinks.map((link) => (
                        <Link key={link.href} href={link.href} className={`${styles.navLink} ${pathname === link.href ? styles.active : ''}`}>
                            {link.label}
                        </Link>
                    ))}
                </nav>

                {/* Actions */}
                <div className={styles.actions}>
                    {isMounted && <CurrencySwitcher />}
                    <button
                        className={styles.cartButton}
                        onClick={toggleCart}
                        aria-label="Open cart"
                    >
                        <ShoppingBag size={22} />
                        {isMounted && totalItems > 0 && (
                            <motion.span
                                className={styles.cartBadge}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                key={totalItems}
                            >
                                {totalItems}
                            </motion.span>
                        )}
                    </button>

                    <Link href="/account/dashboard">
                        <button className={styles.cartButton} aria-label="Account">
                            <User size={22} />
                        </button>
                    </Link>

                    <button
                        className={styles.menuButton}
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label="Toggle menu"
                    >
                        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {/* Mobile Navigation */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.nav
                        className={styles.mobileNav}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                href={link.href}
                                className={`${styles.mobileNavLink} ${pathname === link.href ? styles.active : ''}`}
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {link.label}
                            </Link>
                        ))}
                        <CurrencySwitcher variant="pills" />
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
